import { Component, ViewChildren, ElementRef, QueryList, OnInit, OnDestroy } from '@angular/core';
import { Subject, BehaviorSubject, of } from 'rxjs';
import { Router, ActivatedRoute, NavigationEnd, UrlSegment } from '@angular/router';
import { ItemService, StorageService } from '../services';
import { filter, takeUntil, tap, flatMap, map } from 'rxjs/operators';
import { ItemSummary } from '../summary/itemSummary.component';
import * as moment from 'moment';
import * as _ from 'lodash';
import { ItemContextModel } from '../model';

@Component({
    selector: 'most-read-pane',
    styles: [`
        mat-card {
            margin-bottom: 6px;
        }
        
        .title {
            font-size: 20px;
        }
        
        .card-container {
            height: 100%
        }
        
        .title-item {
            display: inline-block;
        }

        .itemsPanel {
            overflow-y: auto;
            height: calc(100vh - 145px);
        }
    `],
    template: `
        <div fxLayout="column" *ngIf="!showItem" class="card-container">
            <mat-card>
                <mat-card-content>
                    <mat-card-title class="title">
                        <div class="title-item">Most Read Last Week</div>
                        <mat-chip-list *ngIf="totalItems" class="title-item" style="padding-left: 20px;"><mat-chip>{{totalItems}}</mat-chip></mat-chip-list>
                    </mat-card-title>
                    <span fxFlex></span>
                </mat-card-content>
            </mat-card>
            <div *ngIf="items | async as itemContents">
                <div *ngIf="!noTouching && itemContents && !itemContents.length" fxLayout="column" fxLayoutAlign="center center">
                    <h1 class="text-center"><i class="fa fa-search"></i></h1>
                    <div>This will show you the items read by everyone in your team over the last 7 days sorted by number of reads.</div>
                </div>
                <div *ngIf="!noTouching && itemContents && itemContents.length" 
                     class="itemsPanel"
                     infiniteScroll 
                     [infiniteScrollThrottle]="300"
                     [infiniteScrollDistance]="1"
                     [scrollWindow]="false"
                     (scrolled)="retrievePage()">
                    <span *ngFor="let item of itemContents">
                        <item-summary [item]="item" [attr.itemId]="item.id"></item-summary>
                    </span>
                    <div *ngIf="pagingNoTouching" fxLayout="row" fxLayoutAlign="center center" style="padding-top: 25px;">
                        <spinner></spinner>
                    </div>        
                </div>
            </div>
            <div *ngIf="noTouching" fxLayout="row" fxLayoutAlign="center center" style="padding-top: 25px;">
                <spinner></spinner>
            </div>
        </div>
    `
})
export class MostReadPaneComponent implements OnInit, OnDestroy {
    cleanupTime = new Subject<void>();
    searchParams = new BehaviorSubject(null);
    items = new BehaviorSubject([]);
    totalItems: number;
    noTouching: boolean = true;
    pagingNoTouching: boolean = true;
    showItem: boolean = false;
    @ViewChildren(ItemSummary, { read: ElementRef }) itemSummaries: QueryList<ElementRef>;
    
    constructor(private router: Router, private route: ActivatedRoute, private itemService: ItemService, private storageSerice: StorageService){
        router.events
        .pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this.cleanupTime)
        )
        .subscribe(() => {
            this.showItem = !!this.route.firstChild;
            if (!this.showItem) {
                setTimeout(() => {
                    const targetItem = this.storageSerice.retrieve('most-read-item-clicked');
                    if (targetItem && this.itemSummaries) {
                        const targetSummary = this.itemSummaries.find((itemSummary: ElementRef) => itemSummary.nativeElement.getAttribute('itemId') === targetItem);
                        if(targetSummary) {
                            targetSummary.nativeElement.scrollIntoView();
                        }
                    }
                });
            } else {
                this.storageSerice.store('most-read-item-clicked', this.route.firstChild.snapshot.params.itemId);
            }
        });

        this.searchParams
        .pipe(
            filter(params => !!params),
            tap(() => this.pagingNoTouching = true),
            flatMap(params => this.itemService.searchTenantItems(params)),
            takeUntil(this.cleanupTime)
        )
        .subscribe(itemResults => {
            this.totalItems = itemResults.totalElements;
            itemResults.content.forEach(item => item.visible = true);
            this.items.next(this.items.value.concat(itemResults.content));
            this.noTouching = false;
            this.pagingNoTouching = false;
        });
    }

    ngOnInit() {
        this.route.url.pipe(takeUntil(this.cleanupTime)).subscribe((segs: UrlSegment[]) => {
            if (segs.length) {
                this.itemService.focusedItemEdit.next(undefined);
                const itemId = segs[1].path;
                let item = this.items.value.find(item => item.id === itemId);
                const isSource = false; 
                const curating = false;
                if (!item) {
                    this.itemService.getItem(itemId)
                    .pipe(map(response => response.item))
                    .subscribe(fetchedItem => this.itemService.focusedItem.next(new ItemContextModel(fetchedItem, undefined, undefined, undefined, isSource, curating)));
                } else {
                    this.itemService.focusedItem.next(new ItemContextModel(item, undefined, undefined, undefined, isSource, curating));
                }
            } else {
                this.itemService.focusedItem.next(undefined);
            }
        });
        const baseParams = { 
            page: 0,
            sortField: 'readEvents', 
            includeFullDescription: true,
            includeReadItems: true,
            readAfterDateTime: moment().subtract(7, 'days').toISOString() 
        };
        this.itemService.itemClosing.pipe(takeUntil(this.cleanupTime)).subscribe(() => this.router.navigateByUrl(this.itemService.childClearedPath()));
        this.itemService.itemContextChanged.pipe(takeUntil(this.cleanupTime)).subscribe(() => this.searchParams.next(baseParams));
        this.storageSerice.store('most-read-item-clicked', null);
        this.searchParams.next(baseParams);
    }

    retrievePage = () => {
        this.searchParams.next(Object.assign({}, this.searchParams.value, { page: ++this.searchParams.value.page }));
    }

    ngOnDestroy() {
        this.cleanupTime.next();
        this.cleanupTime.complete();
        this.searchParams.complete();
        this.items.complete();
    }
}
